// ─── AuthScreens — Login / Registrazione / VerifyPhone ──────────────────────
// Tre componenti che gestiscono il flusso di autenticazione completo.
// Lo stile riusa i token e le primitive (Btn, Input, Field) di App.js
// importandoli da '../App'.
//
// Flusso:
//   1) Schermata Welcome → Login | Registrazione
//   2) Registrazione → email + password + telefono
//   3) Conferma OTP via SMS
//   4) Account creato (trigger DB scrive profilo + 2 crediti gratuiti)
//   5) Login successive: email + password (telefono opzionale come 2FA futura)

import { useState, useEffect, useRef } from 'react';
import { supabase, SUPABASE_ENABLED } from '../lib/supabase';

// ─── Cloudflare Turnstile — site key letta dall'env ─────────────────────────
// Se non valorizzata, il widget non viene renderizzato e si bypassa il check
// (utile in dev senza Turnstile configurato). In produzione DEVE essere settata.
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// Carica lo script Turnstile una sola volta nel <head>.
let turnstileLoaderPromise = null;
function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (turnstileLoaderPromise) return turnstileLoaderPromise;
  turnstileLoaderPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => resolve(); // non blocchiamo l'app se Turnstile non carica
    document.head.appendChild(s);
  });
  return turnstileLoaderPromise;
}

// Componente widget Turnstile riutilizzabile.
// IMPORTANTE: il widget si monta UNA SOLA VOLTA (effect con [] deps).
// Le callback vengono lette tramite ref così l'effetto non si rimonta mai.
// Senza questo accorgimento il widget sfarfallava ad ogni keystroke nel form.
function TurnstileWidget({ onVerify, onExpire }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const onVerifyRef  = useRef(onVerify);
  const onExpireRef  = useRef(onExpire);

  // Aggiorna i ref ad ogni render senza triggerare l'effetto di sotto
  useEffect(() => { onVerifyRef.current = onVerify; });
  useEffect(() => { onExpireRef.current = onExpire; });

  // Mount/unmount UNA volta sola — il widget non si ricostruisce mai
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return; // non configurato → skip
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback:           (token) => onVerifyRef.current && onVerifyRef.current(token),
        'expired-callback': ()      => onExpireRef.current && onExpireRef.current(),
        'error-callback':   ()      => onExpireRef.current && onExpireRef.current(),
        theme: 'light',
        size:  'flexible',
      });
    });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ← deliberato: una sola volta al mount

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={containerRef} style={{ marginBottom: 16, minHeight: 65 }} />;
}

// ─── Stili condivisi (locali a questo modulo) ──────────────────────────────
const C = {
  bg:        '#f5f3ee',
  surface:   '#ffffff',
  accent:    '#6366f1',
  accent2:   '#8b5cf6',
  accentDeep:'#1e1b4b',
  green:     '#10b981',
  red:       '#ef4444',
  text:      '#1c1917',
  muted:     '#78716c',
  mutedLight:'#a8a29e',
  border:    '#ebe7df',
  orange:    '#f59e0b',
};
const grad = (a, b, deg = 135) => `linear-gradient(${deg}deg, ${a}, ${b})`;
const hex  = (c, a) => c + Math.round(Math.max(0, Math.min(1, a)) * 255).toString(16).padStart(2, '0');

// ─── Card wrapper — sfondo bento con illustrazione laterale ────────────────
function AuthCard({ children, title, subtitle }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${C.bg} 0%, #ece8df 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: "'Inter', sans-serif"
    }}>
      <div style={{
        background: C.surface, borderRadius: 32, padding: 40,
        boxShadow: '0 16px 40px rgba(28,25,23,0.10), 0 4px 12px rgba(28,25,23,0.06)',
        border: `1px solid ${C.border}`, width: '100%', maxWidth: 440,
        animation: 'fadeUp .4s cubic-bezier(.16,1,.3,1)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 64, height: 64, margin: '0 auto 20px', borderRadius: 18,
            background: grad(C.accent, C.accent2),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 12px 28px ${hex(C.accent, 0.35)}`
          }}>
            <img src={`${process.env.PUBLIC_URL || ''}/logo.jpg`} alt="IEP Assistant"
              style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 14 }} />
          </div>
          <h1 style={{
            fontFamily: "'Fraunces', Georgia, serif", fontSize: 28, fontWeight: 700,
            color: C.text, letterSpacing: '-0.02em', marginBottom: 6
          }}>{title}</h1>
          {subtitle && (
            <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>{subtitle}</div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Input riusabile ───────────────────────────────────────────────────────
function FormInput({ label, type='text', value, onChange, placeholder, required, error, autoFocus, hint }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700,
        color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8,
        marginBottom: 8
      }}>
        {label}{required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
      </label>
      <input
        type={type} value={value || ''} onChange={onChange}
        placeholder={placeholder} autoFocus={autoFocus}
        style={{
          width: '100%', padding: '12px 16px',
          background: C.surface,
          border: `1px solid ${error ? C.red : C.border}`,
          borderRadius: 18, fontSize: 14, fontWeight: 500,
          fontFamily: "'Inter', sans-serif", color: C.text,
          transition: 'all .2s', outline: 'none'
        }}
        onFocus={e => { e.target.style.borderColor = C.accent; e.target.style.boxShadow = `0 0 0 4px ${hex(C.accent, 0.12)}`; }}
        onBlur={e => { e.target.style.borderColor = error ? C.red : C.border; e.target.style.boxShadow = 'none'; }}
      />
      {hint && !error && <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 6 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: C.red, marginTop: 6, fontWeight: 500 }}>⚠ {error}</div>}
    </div>
  );
}

// ─── Bottone primario ──────────────────────────────────────────────────────
function PrimaryBtn({ children, onClick, loading, disabled, type = 'button' }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading}
      style={{
        width: '100%', padding: '13px 22px', borderRadius: 999, border: 'none',
        background: disabled ? '#d6d3cc' : grad(C.accent, C.accent2),
        color: '#fff', fontSize: 14, fontWeight: 600,
        fontFamily: "'Inter', sans-serif", cursor: disabled ? 'not-allowed' : 'pointer',
        boxShadow: disabled ? 'none' : `0 8px 22px ${hex(C.accent, 0.35)}`,
        transition: 'all .2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
      }}
    >
      {loading && <span className="spin" style={{
        width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)',
        borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block'
      }} />}
      {children}
    </button>
  );
}

// ─── CodiceFiscaleInput — campo CF italiano con validazione checksum ────────
// Validazione completa secondo D.M. 23/12/1976: 16 caratteri con checksum
// algoritmico (l'ultima lettera è ricavata matematicamente dalle altre 15).
// Se l'utente digita un CF inventato a caso, lo rileviamo immediatamente.
const CF_ODD = {
  '0':1,'1':0,'2':5,'3':7,'4':9,'5':13,'6':15,'7':17,'8':19,'9':21,
  'A':1,'B':0,'C':5,'D':7,'E':9,'F':13,'G':15,'H':17,'I':19,'J':21,
  'K':2,'L':4,'M':18,'N':20,'O':11,'P':3,'Q':6,'R':8,'S':12,'T':14,
  'U':16,'V':10,'W':22,'X':25,'Y':24,'Z':23,
};
const CF_EVEN = {
  '0':0,'1':1,'2':2,'3':3,'4':4,'5':5,'6':6,'7':7,'8':8,'9':9,
  'A':0,'B':1,'C':2,'D':3,'E':4,'F':5,'G':6,'H':7,'I':8,'J':9,
  'K':10,'L':11,'M':12,'N':13,'O':14,'P':15,'Q':16,'R':17,'S':18,'T':19,
  'U':20,'V':21,'W':22,'X':23,'Y':24,'Z':25,
};

function validateCodiceFiscale(cf) {
  if (typeof cf !== 'string') return { ok: false, reason: 'invalid_type' };
  const c = cf.toUpperCase().trim();
  // Formato base: 6 lettere + 2 cifre + 1 lettera + 2 cifre + 1 lettera + 3 cifre + 1 lettera
  if (!/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(c)) {
    return { ok: false, reason: 'invalid_format' };
  }
  // Calcolo checksum: i primi 15 caratteri vanno mappati con tabelle diverse
  // a seconda della posizione (1-based: posizioni dispari → CF_ODD, pari → CF_EVEN)
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const ch = c[i];
    sum += (i % 2 === 0) ? CF_ODD[ch] : CF_EVEN[ch];
  }
  const expectedChecksum = String.fromCharCode('A'.charCodeAt(0) + (sum % 26));
  if (c[15] !== expectedChecksum) {
    return { ok: false, reason: 'invalid_checksum' };
  }
  return { ok: true };
}

// ─── Calcolo codice CF da nome/cognome (D.M. 23/12/1976) ──────────────────
// Normalizza una stringa: maiuscolo, no accenti, no spazi, no caratteri speciali.
function cfNormalize(s) {
  return (s || '')
    .normalize('NFD')                  // decomponi i caratteri accentati (é → e + ´)
    .replace(/[̀-ͯ]/g, '')   // rimuovi i diacritici (combining marks)
    .toUpperCase()
    .replace(/[^A-Z]/g, '');           // tieni solo A-Z
}
function cfConsonants(s) { return s.replace(/[AEIOU]/g, ''); }
function cfVowels(s)     { return s.replace(/[^AEIOU]/g, ''); }

// Codice cognome (positions 0-2 del CF):
// 1) tutte le consonanti in ordine, 2) poi le vocali, 3) padding 'X'
function cfSurnameCode(surname) {
  const norm = cfNormalize(surname);
  if (!norm) return '';
  let r = (cfConsonants(norm) + cfVowels(norm)).slice(0, 3);
  while (r.length < 3) r += 'X';
  return r;
}

// Codice nome (positions 3-5 del CF):
// Se ci sono ≥4 consonanti → 1ª, 3ª, 4ª. Altrimenti come il cognome.
function cfNameCode(name) {
  const norm = cfNormalize(name);
  if (!norm) return '';
  const c = cfConsonants(norm);
  if (c.length >= 4) return c[0] + c[2] + c[3];
  let r = (c + cfVowels(norm)).slice(0, 3);
  while (r.length < 3) r += 'X';
  return r;
}

// Confronta nome+cognome inseriti con i primi 6 caratteri del CF.
// Restituisce { ok: bool, expected: '...', got: '...' }.
function checkNameMatchesCF(firstName, lastName, cf) {
  if (!cf || cf.length < 6) return { ok: true }; // CF ancora incompleto: skip
  if (!firstName || !lastName) return { ok: true }; // dati incompleti: skip
  const expectedSurname = cfSurnameCode(lastName);
  const expectedName    = cfNameCode(firstName);
  const cfUpper = cf.toUpperCase();
  const gotSurname = cfUpper.slice(0, 3);
  const gotName    = cfUpper.slice(3, 6);
  return {
    ok: expectedSurname === gotSurname && expectedName === gotName,
    expected: expectedSurname + expectedName,
    got: gotSurname + gotName,
  };
}

function CodiceFiscaleInput({ value, onChange, error, hint }) {
  const handleChange = (e) => {
    // Auto-uppercase, rimuovi caratteri non alfanumerici, max 16 char
    let v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
    onChange(v);
  };

  // Validazione live (mostra ✓ quando il CF è valido)
  const isComplete = value && value.length === 16;
  const validation = isComplete ? validateCodiceFiscale(value) : null;
  const isValid = validation?.ok === true;

  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700,
        color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8,
        marginBottom: 8
      }}>
        Codice Fiscale<span style={{ color: C.red, marginLeft: 3 }}>*</span>
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          inputMode="text"
          autoComplete="off"
          spellCheck="false"
          autoCapitalize="characters"
          value={value || ''}
          onChange={handleChange}
          placeholder="RSSMRA85M01H501Z"
          maxLength={16}
          style={{
            width: '100%', padding: '12px 16px', paddingRight: 44,
            background: C.surface,
            border: `1px solid ${error ? C.red : (isValid ? C.green : C.border)}`,
            borderRadius: 18, fontSize: 14, fontWeight: 500,
            fontFamily: "'Inter', sans-serif", color: C.text,
            letterSpacing: '0.05em', textTransform: 'uppercase',
            transition: 'all .2s', outline: 'none'
          }}
          onFocus={e => {
            if (!error && !isValid) {
              e.target.style.borderColor = C.accent;
              e.target.style.boxShadow = `0 0 0 4px ${hex(C.accent, 0.12)}`;
            }
          }}
          onBlur={e => {
            e.target.style.boxShadow = 'none';
            e.target.style.borderColor = error ? C.red : (isValid ? C.green : C.border);
          }}
        />
        {isComplete && (
          <span style={{
            position: 'absolute', right: 14, top: '50%',
            transform: 'translateY(-50%)',
            fontSize: 18, color: isValid ? C.green : C.red,
            pointerEvents: 'none'
          }}>{isValid ? '✓' : '✗'}</span>
        )}
      </div>
      {hint && !error && !isComplete && (
        <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 6 }}>{hint}</div>
      )}
      {error && (
        <div style={{ fontSize: 12, color: C.red, marginTop: 6, fontWeight: 500 }}>⚠ {error}</div>
      )}
      {isComplete && !isValid && !error && (
        <div style={{ fontSize: 12, color: C.red, marginTop: 6, fontWeight: 500 }}>
          ⚠ Codice fiscale non valido (controlla che sia digitato correttamente)
        </div>
      )}
    </div>
  );
}

// ─── PhoneInputIT — input telefono con prefisso +39 fisso ───────────────────
// L'utente vede 🇮🇹 +39 a sinistra (read-only) e digita solo il numero.
// Pulizia automatica all'incolla: rimuove +39, 0039, 39, spazi, trattini, parentesi.
// Validazione: 8-12 cifre dopo il prefisso (range numeri italiani validi).
function PhoneInputIT({ value, onChange, error, autoFocus, hint }) {
  const handleChange = (e) => {
    let v = e.target.value;
    // Rimuovi caratteri non numerici (mantieni solo cifre e spazi per leggibilità)
    v = v.replace(/[^\d\s]/g, '');
    // Auto-strip se incolla un numero con prefisso completo o parziale
    const compact = v.replace(/\s/g, '');
    let stripped = compact;
    if (stripped.startsWith('0039')) stripped = stripped.slice(4);
    else if (stripped.startsWith('39') && stripped.length >= 11) stripped = stripped.slice(2);
    // Limita a 12 cifre max (numeri ITA mobile/fisso valgono fino a 11)
    stripped = stripped.slice(0, 12);
    onChange(stripped);
  };

  // Formattazione visiva: "333 1234567" invece di "3331234567"
  const formatted = value
    ? (value.length > 3 ? value.slice(0, 3) + ' ' + value.slice(3) : value)
    : '';

  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 700,
        color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8,
        marginBottom: 8
      }}>
        Numero di cellulare<span style={{ color: C.red, marginLeft: 3 }}>*</span>
      </label>
      <div style={{
        display: 'flex', alignItems: 'stretch',
        border: `1px solid ${error ? C.red : C.border}`,
        borderRadius: 18,
        background: C.surface,
        overflow: 'hidden',
        transition: 'all .2s',
      }}>
        {/* Prefisso fisso */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 14px',
          background: '#faf9f6',
          borderRight: `1px solid ${C.border}`,
          fontSize: 14, fontWeight: 600, color: C.text,
          userSelect: 'none', flexShrink: 0,
        }}>
          <span style={{ fontSize: 18 }}>🇮🇹</span>
          <span>+39</span>
        </div>
        {/* Input numero */}
        <input
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={formatted}
          onChange={handleChange}
          autoFocus={autoFocus}
          placeholder="333 1234567"
          style={{
            flex: 1, border: 'none', outline: 'none',
            padding: '12px 16px', fontSize: 14, fontWeight: 500,
            fontFamily: "'Inter', sans-serif", color: C.text,
            background: 'transparent', minWidth: 0,
          }}
          onFocus={e => {
            e.target.parentElement.style.borderColor = C.accent;
            e.target.parentElement.style.boxShadow = `0 0 0 4px ${hex(C.accent, 0.12)}`;
          }}
          onBlur={e => {
            e.target.parentElement.style.borderColor = error ? C.red : C.border;
            e.target.parentElement.style.boxShadow = 'none';
          }}
        />
      </div>
      {hint && !error && <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 6 }}>{hint}</div>}
      {error && <div style={{ fontSize: 12, color: C.red, marginTop: 6, fontWeight: 500 }}>⚠ {error}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  WELCOME — landing iniziale: scegli login o registrazione
// ═══════════════════════════════════════════════════════════════════════════
export function WelcomeScreen({ onLogin, onRegister }) {
  return (
    <AuthCard
      title="Benvenuto in IEP Assistant"
      subtitle="L'assistente intelligente per la compilazione del PEI ministeriale"
    >
      <PrimaryBtn onClick={onLogin}>Accedi</PrimaryBtn>
      <div style={{ height: 12 }} />
      <button onClick={onRegister}
        style={{
          width: '100%', padding: '13px 22px', borderRadius: 999,
          background: 'transparent', border: `1px solid ${C.border}`,
          color: C.text, fontSize: 14, fontWeight: 600,
          fontFamily: "'Inter', sans-serif", cursor: 'pointer', transition: 'all .2s'
        }}
      >Crea account · 2 crediti gratuiti</button>

      <div style={{ marginTop: 24, padding: '14px 18px',
        background: hex(C.accent, 0.05), border: `1px solid ${hex(C.accent, 0.15)}`,
        borderRadius: 14, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
        <strong style={{ color: C.accent }}>Come funziona?</strong> Registrandoti ricevi 2 crediti gratuiti per provare l'AI. Poi acquista un pacchetto: <strong>Base €15 / 60 crediti</strong> oppure <strong>Premium €28 / 120 crediti</strong>.
      </div>
    </AuthCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  LOGIN — email + password
// ═══════════════════════════════════════════════════════════════════════════
export function LoginScreen({ onBack, onSuccess, onForgotPassword }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Inserisci email e password'); return; }
    setLoading(true);
    setError('');
    if (!SUPABASE_ENABLED) {
      setError('Auth non configurato. Imposta REACT_APP_SUPABASE_URL nel file .env');
      setLoading(false); return;
    }
    const { data, error: err } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: { captchaToken: captchaToken || undefined },
    });
    setLoading(false);
    if (err) {
      setError(err.message === 'Invalid login credentials' ? 'Email o password errati' : err.message);
      if (window.turnstile) try { window.turnstile.reset(); } catch (_) {}
      setCaptchaToken('');
      return;
    }
    if (data?.user) onSuccess(data.user);
  };

  return (
    <AuthCard title="Accedi" subtitle="Inserisci le credenziali del tuo account">
      <form onSubmit={handleLogin}>
        <FormInput label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="docente@scuola.it" required autoFocus />
        <FormInput label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="••••••••" required error={error} />

        <TurnstileWidget onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />

        <PrimaryBtn type="submit" loading={loading}>Accedi</PrimaryBtn>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18, fontSize: 12 }}>
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', padding: 4 }}>
            ← Indietro
          </button>
          <button type="button" onClick={onForgotPassword}
            style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', padding: 4, fontWeight: 600 }}>
            Password dimenticata?
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  FORGOT PASSWORD — chiede email e invia link di reset via Supabase
// ═══════════════════════════════════════════════════════════════════════════
export function ForgotPasswordScreen({ onBack }) {
  const [email, setEmail]           = useState('');
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);
  const [sent, setSent]             = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email) { setError('Inserisci la tua email'); return; }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Completa il controllo anti-bot prima di procedere'); return;
    }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken: captchaToken || undefined,
    });
    setLoading(false);
    if (err) {
      setError('Errore: ' + err.message);
      if (window.turnstile) try { window.turnstile.reset(); } catch (_) {}
      setCaptchaToken('');
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <AuthCard title="Email inviata" subtitle="Controlla la tua casella di posta">
        <div style={{
          textAlign: 'center', padding: '24px 0',
          background: hex(C.green, 0.08), borderRadius: 18,
          border: `1px solid ${hex(C.green, 0.2)}`, marginBottom: 24
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📧</div>
          <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, padding: '0 8px' }}>
            Abbiamo inviato un link per reimpostare la password a <br />
            <strong style={{ color: C.text }}>{email}</strong>.<br /><br />
            Clicca il link nell'email entro <strong>1 ora</strong>.
          </div>
        </div>
        <PrimaryBtn onClick={onBack}>← Torna al login</PrimaryBtn>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Password dimenticata" subtitle="Ti inviamo un link per reimpostarla">
      <form onSubmit={handleSubmit}>
        <FormInput
          label="Email del tuo account"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="docente@scuola.it"
          required
          autoFocus
          error={error}
        />

        <TurnstileWidget onVerify={setCaptchaToken} onExpire={() => setCaptchaToken('')} />

        <PrimaryBtn type="submit" loading={loading}
          disabled={TURNSTILE_SITE_KEY && !captchaToken}>
          Invia link di reset
        </PrimaryBtn>
        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12 }}>
            ← Torna al login
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  RESET PASSWORD — nuova password dopo aver cliccato il link email
// ═══════════════════════════════════════════════════════════════════════════
export function ResetPasswordScreen({ onSuccess }) {
  const [password, setPassword]   = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password.length < 8) { setError('La password deve essere di almeno 8 caratteri'); return; }
    if (password !== password2) { setError('Le due password non coincidono'); return; }
    setLoading(true); setError('');
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) { setError('Errore: ' + err.message); return; }
    setDone(true);
    setTimeout(() => onSuccess && onSuccess(), 2000);
  }

  if (done) {
    return (
      <AuthCard title="Password aggiornata!" subtitle="Verrai reindirizzato automaticamente">
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: 48 }}>✅</div>
          <div style={{ fontSize: 14, color: C.muted, marginTop: 12 }}>
            La tua password è stata aggiornata con successo.
          </div>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Nuova password" subtitle="Scegli una password sicura">
      <form onSubmit={handleSubmit}>
        <FormInput
          label="Nuova password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Almeno 8 caratteri"
          required
          autoFocus
          hint="Almeno 8 caratteri"
        />
        <FormInput
          label="Conferma password"
          type="password"
          value={password2}
          onChange={e => setPassword2(e.target.value)}
          placeholder="Ripeti la password"
          required
          error={error}
        />
        <PrimaryBtn type="submit" loading={loading}>Salva nuova password</PrimaryBtn>
      </form>
    </AuthCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  REGISTRAZIONE — email + password + telefono → invia OTP
// ═══════════════════════════════════════════════════════════════════════════
// Ordine di scuola dichiarato in registrazione → fascia (band) del profilo.
const ORDINE_LABELS = { infanzia: 'Infanzia', primaria: 'Primaria', secondaria1: 'Secondaria I grado', secondaria2: 'Secondaria II grado' };
const DOMINI_EMAIL_NOTI = [
  'gmail.com', 'libero.it', 'hotmail.it', 'hotmail.com', 'outlook.it', 'outlook.com',
  'yahoo.it', 'yahoo.com', 'icloud.com', 'tiscali.it', 'virgilio.it', 'alice.it',
  'live.it', 'live.com', 'email.it', 'tin.it', 'istruzione.it', 'posta.istruzione.it',
];

const DOMINI_EMAIL_ERRATI = {
  'gmail.it': 'gmail.com',
  'gmail.co': 'gmail.com',
  'libero.com': 'libero.it',
  'virgilio.com': 'virgilio.it',
  'alice.com': 'alice.it',
  'tiscali.com': 'tiscali.it',
  'istruzione.com': 'istruzione.it',
};

function distanzaRefuso(a, b) {
  const m = a.length, n = b.length;
  const d = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + costo);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
      }
    }
  }
  return d[m][n];
}

function suggerisciEmail(email) {
  const v = String(email || '').trim().toLowerCase();
  const at = v.lastIndexOf('@');
  if (at < 1 || at === v.length - 1) return null;
  const locale = v.slice(0, at);
  const dominio = v.slice(at + 1);
  if (!dominio.includes('.') || DOMINI_EMAIL_NOTI.includes(dominio)) return null;
  const esplicito = DOMINI_EMAIL_ERRATI[dominio];
  if (esplicito) return `${locale}@${esplicito}`;
  for (const noto of DOMINI_EMAIL_NOTI) {
    if (distanzaRefuso(dominio, noto) === 1) return `${locale}@${noto}`;
  }
  return null;
}

const ORDINE_TO_BAND = { infanzia: 'inf_pri', primaria: 'inf_pri', secondaria1: 'sec1', secondaria2: 'sec2' };
// Ordini effettivamente selezionabili in registrazione. La SECONDARIA DI II GRADO
// è temporaneamente ESCLUSA dalla vendita: l'app non raccoglie l'indirizzo di studio
// e la progettazione disciplinare non è ancora allineata agli altri ordini.
// Resta pienamente funzionante per i TESTER, a cui la band `sec2` viene impostata a
// mano via SQL. Le mappe qui sopra restano complete apposta: riabilitare l'ordine è
// questione di rimettere la chiave in questo elenco.
const ORDINI_REGISTRABILI = ['infanzia', 'primaria', 'secondaria1'];

export function RegisterScreen({ onBack, onSuccess }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [codiceFiscale, setCF]    = useState('');
  const [ordineScuola, setOrdineScuola] = useState(''); // infanzia|primaria|secondaria1|secondaria2 → band
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [generalError, setGenError] = useState('');
  const [captchaToken, setCaptchaToken] = useState(''); // Turnstile
  const [emailSent, setEmailSent] = useState(false);    // dopo signup, mostra "controlla email"

  // Match live nome/cognome ↔ CF: usato per mostrare warning sotto i campi
  // mentre l'utente digita (non blocca: la validazione hard è solo al submit).
  const nameMatch = checkNameMatchesCF(firstName, lastName, codiceFiscale);

  const emailSuggerita = suggerisciEmail(email);

  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.firstName = 'Nome obbligatorio';
    if (!lastName.trim())  e.lastName  = 'Cognome obbligatorio';
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = 'Email non valida';
    if (password.length < 8) e.password = 'Almeno 8 caratteri';
    const cfCheck = validateCodiceFiscale(codiceFiscale);
    if (!cfCheck.ok) {
      e.codiceFiscale = cfCheck.reason === 'invalid_format'
        ? 'Codice fiscale incompleto (16 caratteri)'
        : 'Codice fiscale non valido (checksum errato)';
    } else if (firstName.trim() && lastName.trim()) {
      // CF valido + nome/cognome inseriti → verifica che corrispondano
      const m = checkNameMatchesCF(firstName, lastName, codiceFiscale);
      if (!m.ok) {
        e.codiceFiscale =
          `Il codice fiscale non corrisponde al nome inserito. Atteso "${m.expected}", trovato "${m.got}". Controlla nome, cognome e CF.`;
      }
    }
    if (!ordineScuola) e.ordineScuola = 'Seleziona l\'ordine di scuola in cui insegni';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleRegister = async (ev) => {
    ev.preventDefault();
    setGenError('');
    if (!validate()) return;
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setGenError('Completa il controllo anti-bot prima di procedere'); return;
    }
    if (!SUPABASE_ENABLED) {
      setGenError('Auth non configurato. Imposta REACT_APP_SUPABASE_URL nel file .env'); return;
    }
    setLoading(true);
    // Signup con email + password + CF (no telefono).
    // Supabase invia automaticamente l'email di conferma.
    // Il codice fiscale viene salvato in raw_user_meta_data e poi copiato dal
    // trigger handle_new_user nella tabella profiles (con vincolo unique).
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: `${firstName.trim()} ${lastName.trim()}`,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          codice_fiscale: codiceFiscale.toUpperCase().trim(),
          band: ORDINE_TO_BAND[ordineScuola] || null,
          ordine_dichiarato: ordineScuola || null,
        },
        captchaToken: captchaToken || undefined,
        emailRedirectTo: window.location.origin,
      },
    });
    setLoading(false);
    if (error) {
      // Errore tipico: "duplicate key value violates unique constraint"
      // se il CF è già usato da un altro account
      if (/codice_fiscale|cf_unique|duplicate/i.test(error.message)) {
        setGenError('Questo codice fiscale è già registrato. Se hai già un account, accedi.');
      } else if (/already registered|already exists/i.test(error.message)) {
        setGenError('Questa email è già registrata. Vai a "Accedi".');
      } else {
        setGenError(error.message);
      }
      if (window.turnstile) try { window.turnstile.reset(); } catch (_) {}
      setCaptchaToken('');
      return;
    }
    // Se la conferma email è abilitata in Supabase, l'utente NON è ancora
    // loggato: deve cliccare il link nell'email. Mostriamo schermata di attesa.
    if (data?.user && !data.session) {
      setEmailSent(true);
      return;
    }
    // Altrimenti già loggato → vai dritto in app
    if (data?.session) onSuccess(data.user);
  };

  // Schermata "controlla email" dopo signup riuscito
  if (emailSent) {
    return (
      <AuthCard title="Controlla la tua email" subtitle="Ti abbiamo inviato un link per attivare l'account">
        <div style={{
          padding: '20px 22px', background: hex(C.green, 0.06),
          border: `1px solid ${hex(C.green, 0.20)}`, borderRadius: 16,
          marginBottom: 20, textAlign: 'center'
        }}>
          <div style={{ fontSize: 38, marginBottom: 10 }}>📬</div>
          <div style={{ fontSize: 14, color: C.text, fontWeight: 500, lineHeight: 1.6 }}>
            Email inviata a <strong style={{ color: C.green }}>{email}</strong>.<br/>
            Clicca il link nell'email per confermare e ricevere i tuoi <strong>2 crediti gratuiti</strong>.
          </div>
        </div>
        <div style={{ fontSize: 12, color: C.mutedLight, lineHeight: 1.6, marginBottom: 16 }}>
          Non vedi l'email? Controlla la cartella <strong>Spam/Promozioni</strong>. Il link scade dopo 24 ore.
          <br />
          Se l'indirizzo qui sopra non è il tuo, scrivici a{' '}
          <a href="mailto:supporto@iep-assistant.it"
            style={{ color: C.accent, fontWeight: 600, textDecoration: 'none' }}>
            supporto@iep-assistant.it
          </a>{' '}e lo correggiamo noi.
        </div>
        <div style={{ textAlign: 'center', fontSize: 12 }}>
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: 'none', color: C.accent, cursor: 'pointer', fontWeight: 600 }}>
            ← Torna alla schermata di accesso
          </button>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Crea account" subtitle="2 crediti gratuiti per iniziare">
      <form onSubmit={handleRegister}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <FormInput label="Nome" value={firstName} onChange={e => setFirstName(e.target.value)}
            placeholder="Mario" required error={errors.firstName} autoFocus />
          <FormInput label="Cognome" value={lastName} onChange={e => setLastName(e.target.value)}
            placeholder="Rossi" required error={errors.lastName} />
        </div>
        <FormInput label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="docente@scuola.it" required error={errors.email} />
        {emailSuggerita && !errors.email && (
          <div style={{ marginTop: -8, marginBottom: 14, fontSize: 12, color: C.orange,
            fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>⚠ Forse intendevi <strong>{emailSuggerita}</strong>?</span>
            <button type="button" onClick={() => setEmail(emailSuggerita)}
              style={{ padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                border: `1px solid ${hex(C.orange, 0.5)}`, background: hex(C.orange, 0.08),
                color: C.orange, fontSize: 11, fontWeight: 600, fontFamily: "'Inter',sans-serif" }}>
              Correggi
            </button>
          </div>
        )}
        <FormInput label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="Almeno 8 caratteri" required error={errors.password}
          hint="Minimo 8 caratteri" />
        <CodiceFiscaleInput
          value={codiceFiscale}
          onChange={setCF}
          error={errors.codiceFiscale}
          hint="16 caratteri — verifichiamo la corrispondenza con nome e cognome"
        />
        {/* Feedback live di match nome ↔ CF (mostra solo se il CF è completo) */}
        {codiceFiscale.length === 16 && firstName.trim() && lastName.trim() && (
          nameMatch.ok ? (
            <div style={{ marginTop: -8, marginBottom: 14, fontSize: 12,
              color: C.green, fontWeight: 500 }}>
              ✓ Codice fiscale coerente con nome e cognome
            </div>
          ) : (
            <div style={{ marginTop: -8, marginBottom: 14, fontSize: 12,
              color: C.red, fontWeight: 500 }}>
              ⚠ Il codice fiscale non corrisponde a "{firstName} {lastName}"
            </div>
          )
        )}

        {/* Ordine di scuola → determina la fascia (band) e i pacchetti disponibili */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
            Ordine di scuola in cui insegni <span style={{ color: C.red }}>*</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {ORDINI_REGISTRABILI.map(k => [k, ORDINE_LABELS[k]]).map(([k, label]) => (
              <button type="button" key={k} onClick={() => setOrdineScuola(k)}
                style={{ padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, transition: 'all .15s',
                  border: `2px solid ${ordineScuola === k ? C.accent : C.border}`,
                  background: ordineScuola === k ? hex(C.accent, 0.08) : C.bg,
                  color: ordineScuola === k ? C.accent : C.text }}>
                {label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 6, lineHeight: 1.5 }}>
            Determina i pacchetti di crediti disponibili. Chi insegna all'infanzia o alla primaria può creare PEI per entrambi gli ordini.
          </div>
          {/* Secondaria di II grado: non in vendita, ma la porta resta aperta ai tester
              (un docente delle superiori che non trova il suo ordine è un contatto perso). */}
          <div style={{ fontSize: 11, color: C.mutedLight, marginTop: 6, lineHeight: 1.5,
            padding: '8px 10px', borderRadius: 8, background: hex(C.accent, 0.05),
            border: `1px solid ${hex(C.accent, 0.18)}` }}>
            Insegni alla <strong>secondaria di II grado</strong>? Stiamo mettendo a punto il supporto
            per gli indirizzi delle superiori, che per ora non è in vendita. Se vuoi provarlo in
            anteprima e darci un riscontro, scrivici a{' '}
            <a href="mailto:supporto@iep-assistant.it?subject=Sperimentazione%20secondaria%20di%20II%20grado"
               style={{ color: C.accent, fontWeight: 600 }}>supporto@iep-assistant.it</a>.
          </div>
          {errors.ordineScuola && (
            <div style={{ fontSize: 12, color: C.red, marginTop: 6 }}>⚠ {errors.ordineScuola}</div>
          )}
        </div>

        {/* Cloudflare Turnstile — anti-bot (gratis, sostituisce reCAPTCHA) */}
        <TurnstileWidget
          onVerify={setCaptchaToken}
          onExpire={() => setCaptchaToken('')}
        />

        {generalError && (
          <div style={{ padding: '10px 14px', background: hex(C.red, 0.08),
            border: `1px solid ${hex(C.red, 0.25)}`, borderRadius: 12,
            fontSize: 12, color: C.red, marginBottom: 14 }}>
            ⚠ {generalError}
          </div>
        )}

        <PrimaryBtn type="submit" loading={loading}
          disabled={TURNSTILE_SITE_KEY && !captchaToken}>
          Crea account
        </PrimaryBtn>

        <div style={{ marginTop: 18, fontSize: 11, color: C.mutedLight, lineHeight: 1.5 }}>
          Procedendo accetti i Termini di servizio e l'Informativa sulla privacy. Il codice fiscale è richiesto per identificare univocamente l'utente (anti-frode) ed eventuali fatturazioni.
        </div>

        <div style={{ marginTop: 14, textAlign: 'center', fontSize: 12 }}>
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer' }}>
            ← Indietro
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  VERIFICA OTP — input 6 cifre per confermare il telefono
// ═══════════════════════════════════════════════════════════════════════════
export function VerifyPhoneScreen({ phone, onBack, onSuccess }) {
  const [otp, setOtp]         = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) { setError('Inserisci il codice a 6 cifre'); return; }
    setLoading(true); setError('');
    if (!SUPABASE_ENABLED) {
      setError('Auth non configurato.'); setLoading(false); return;
    }
    const { data, error: err } = await supabase.auth.verifyOtp({
      phone, token: otp, type: 'sms',
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    if (data?.user) onSuccess(data.user);
  };

  const resend = async () => {
    if (resendIn > 0) return;
    if (!SUPABASE_ENABLED) return;
    await supabase.auth.resend({ type: 'sms', phone });
    setResendIn(60);
    const t = setInterval(() => {
      setResendIn(prev => {
        if (prev <= 1) { clearInterval(t); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  return (
    <AuthCard title="Verifica il telefono" subtitle={`Abbiamo inviato un codice a 6 cifre al numero ${phone}`}>
      <form onSubmit={handleVerify}>
        <FormInput label="Codice OTP" value={otp}
          onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456" required autoFocus error={error}
          hint="Hai 5 minuti per inserirlo" />

        <PrimaryBtn type="submit" loading={loading} disabled={otp.length !== 6}>
          Conferma e ricevi 2 crediti gratuiti
        </PrimaryBtn>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 18, fontSize: 12 }}>
          <button type="button" onClick={onBack}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer' }}>
            ← Indietro
          </button>
          <button type="button" onClick={resend} disabled={resendIn > 0}
            style={{
              background: 'none', border: 'none',
              color: resendIn > 0 ? C.mutedLight : C.accent,
              cursor: resendIn > 0 ? 'not-allowed' : 'pointer',
              fontWeight: 600
            }}>
            {resendIn > 0 ? `Rinvia tra ${resendIn}s` : 'Rinvia codice'}
          </button>
        </div>
      </form>
    </AuthCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  AuthFlow — orchestratore: 3 schermate (welcome/login/register).
//  La VerifyPhoneScreen non è più nel flow di default — viene riservata
//  alla Phase 2 (richiamata solo per signup flaggati come sospetti).
// ═══════════════════════════════════════════════════════════════════════════
export function AuthFlow({ onAuthenticated }) {
  // Controlla se l'utente arriva dal link di reset password nell'email
  const isPasswordRecovery = window.location.hash.includes('type=recovery') ||
    new URLSearchParams(window.location.search).get('type') === 'recovery';

  const [stage, setStage] = useState(
    isPasswordRecovery ? 'reset' : 'welcome'
  ); // 'welcome' | 'login' | 'register' | 'forgot' | 'reset'

  // Supabase manda evento PASSWORD_RECOVERY quando il token è valido
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setStage('reset');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  if (stage === 'reset') {
    return <ResetPasswordScreen onSuccess={() => {
      // Pulisce l'hash dall'URL e porta al login
      window.history.replaceState({}, document.title, window.location.pathname);
      setStage('login');
    }} />;
  }
  if (stage === 'forgot') {
    return <ForgotPasswordScreen onBack={() => setStage('login')} />;
  }
  if (stage === 'welcome') {
    return <WelcomeScreen
      onLogin={() => setStage('login')}
      onRegister={() => setStage('register')}
    />;
  }
  if (stage === 'login') {
    return <LoginScreen
      onBack={() => setStage('welcome')}
      onSuccess={onAuthenticated}
      onForgotPassword={() => setStage('forgot')}
    />;
  }
  if (stage === 'register') {
    return <RegisterScreen
      onBack={() => setStage('welcome')}
      onSuccess={onAuthenticated}
    />;
  }
  return null;
}
